/**
 * Locale plumbing shared by the React app and (mirrored in) web/seo-content.mjs.
 *
 * URL shape: the default locale stays at the root (`/faq`) and other locales take a
 * prefix (`/tr/faq`). Moving English under `/en/` would have thrown away the ranking
 * history of every existing URL, so the root is left exactly where it is.
 *
 * Path segments are NOT translated (`/tr/valorant-crosshair-generator`, not
 * `/tr/valorant-crosshair-olusturucu`). A per-locale path table would have to be kept
 * in sync across routing, canonicals, redirects, prerender and the sitemap; the keyword
 * carries far more weight in the title, H1 and body, which are translated.
 */

export const DEFAULT_LOCALE = "en";
export const SUPPORTED_LOCALES = ["en", "tr", "ru"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Locales whose translation is complete enough to be indexed, linked and listed in the
 * sitemap. A locale that is routable but not yet written stays out of hreflang and is
 * served noindex — a half-translated page in Yandex's main market is worse than none.
 * Add a locale here only once its content has actually landed.
 */
export const PUBLISHED_LOCALES: readonly Locale[] = ["en", "tr", "ru"];

export function isPublishedLocale(locale: Locale): boolean {
    return PUBLISHED_LOCALES.includes(locale);
}

export function isLocale(value: string): value is Locale {
    return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Splits `/tr/faq` into `{ locale: "tr", basePath: "/faq" }`; `/faq` yields the default. */
export function splitLocale(pathname: string): { locale: Locale; basePath: string } {
    const m = /^\/([a-z]{2})(?=\/|$)/.exec(pathname);
    if (m && isLocale(m[1])) {
        const basePath = pathname.slice(m[0].length) || "/";
        return { locale: m[1], basePath: basePath.startsWith("/") ? basePath : `/${basePath}` };
    }
    return { locale: DEFAULT_LOCALE, basePath: pathname || "/" };
}

/** Builds the URL for a base path in a given locale. */
export function localizedPath(basePath: string, locale: Locale): string {
    const clean = basePath.startsWith("/") ? basePath : `/${basePath}`;
    if (locale === DEFAULT_LOCALE) return clean;
    return clean === "/" ? `/${locale}` : `/${locale}${clean}`;
}
